import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ListPage from "../../../components/ListPage";
import EmptyValue from "../../../components/EmptyValue";
import useFinanceOrders from "../../hooks/useFinanceOrders";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import { FinanceTabs, NamePhoneCell, joinPaymentField, formatCurrency } from "../shared";
import { formatShortDate } from "../../../utils/format";
import OrderStatusFilter from "../../../components/OrderStatusFilter";
import { parseStatusFilterParam, statusesForFilters } from "../../../constants/order-status-filters";

// Finance Orders tab (§2). Columns and order match the spec exactly; checkbox/#/State/
// Status/Channel/Source columns from the prototype's `baseColumns` that carry no new
// finance-specific meaning are kept as plain reads. Sub-tabs (To Be Processed/DRX
// Created/Ready to Dispatch/All Orders) are NOT ported — they aren't mapped to real
// order statuses anywhere in the spec, and Phase 6 only requires column-level fidelity
// (flagged as a scope-reduction DEFAULT).
const PARAM = { page: "page", status: "status" };

function FinanceOrdersPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    const statusFilters = parseStatusFilterParam(searchParams.get(PARAM.status));
    const query = useFinanceOrders({ page, size: DEFAULT_PAGE_SIZE, statuses: statusesForFilters(statusFilters) });
    const pagination = query.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };
    const rows = query.data?.orders ?? [];

    const columns = useMemo(
        () => [
            { key: "id", label: "ID" },
            {
                key: "name",
                label: "Name",
                render: (row) => <NamePhoneCell mapUserPatient={row.mapUserPatient} />,
            },
            { key: "endDate", label: "End Date", render: (row) => formatShortDate(row.endDate) },
            {
                key: "financeTotalAmount",
                label: "Total Amount",
                align: "right",
                render: (row) => formatCurrency(row.financeTotalAmount),
            },
            {
                // §2 LOCKED relabel — absolute rupees from eVital, never a percentage.
                key: "discount",
                label: "Discount (₹)",
                align: "right",
                render: (row) => formatCurrency(row.discount),
            },
            {
                key: "financeOutstanding",
                label: "Outstanding",
                align: "right",
                render: (row) => formatCurrency(row.financeOutstanding),
            },
            {
                key: "inventoryId",
                label: "Evital No",
                render: (row) => row.inventoryId || <EmptyValue />,
            },
            {
                key: "financeEvitalPrintUrl",
                label: "Evital Print",
                render: (row) =>
                    row.financeEvitalPrintUrl ? (
                        <a
                            href={row.financeEvitalPrintUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-[var(--ops-emerald-600)] hover:underline"
                        >
                            View →
                        </a>
                    ) : (
                        <EmptyValue />
                    ),
            },
            {
                key: "gatewayName",
                label: "Payment Gateway",
                render: (row) => joinPaymentField(row.payments, "gatewayName"),
            },
            {
                key: "paymentMethod",
                label: "Payment Method",
                render: (row) => joinPaymentField(row.payments, "paymentMethod"),
            },
            {
                key: "paymentSource",
                label: "Payment Source",
                render: (row) => joinPaymentField(row.payments, "paymentSource"),
            },
            {
                key: "paymentStatus",
                label: "Payment Status",
                render: (row) => joinPaymentField(row.payments, "status"),
            },
            {
                key: "state",
                label: "State",
                render: (row) => row.address?.state || <EmptyValue />,
            },
            { key: "status", label: "Status" },
            { key: "channel", label: "Channel", render: (row) => row.channel || <EmptyValue /> },
            { key: "source", label: "Source", render: (row) => row.source || <EmptyValue /> },
        ],
        []
    );

    return (
        <>
            <FinanceTabs />
            <ListPage
                testId="finance-orders"
                title="Orders"
                subtitle="Order-level finance view — amounts computed from payment items, not userOrders.total."
                filters={
                    <OrderStatusFilter
                        selected={statusFilters}
                        onChange={(ids) => {
                            // A new filter is a different result set -- back to page 1.
                            const next = new URLSearchParams(searchParams);
                            if (ids.length) next.set(PARAM.status, ids.join(","));
                            else next.delete(PARAM.status);
                            next.delete(PARAM.page);
                            setSearchParams(next, { replace: true });
                        }}
                    />
                }
                columns={columns}
                rows={rows}
                query={query}
                pagination={pagination}
                onPageChange={(nextPage) => {
                    const next = new URLSearchParams(searchParams);
                    next.set(PARAM.page, String(nextPage));
                    setSearchParams(next, { replace: true });
                }}
                emptyTitle="No orders"
                emptyHint="No orders found for this page."
            />
        </>
    );
}

export default FinanceOrdersPage;
